import { images } from '_/assets';
import { Button, Header, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { colors, defaultShadow, fontSizes } from '_/config/theme';
import { useVisit } from '_/hooks/VisitContext';
import { useNextCheckinScreen } from '_/hooks/useNextCheckinScreen';
import { AppRoute, CheckinProps } from '_/navigation/types';

import { CameraType, CameraView, useCameraPermissions } from 'expo-camera';

import { BlurView } from 'expo-blur';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Image,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { showMessage } from 'react-native-flash-message';

import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { useNavigation } from '@react-navigation/native';
import { ValidationType } from '@w3lcome/types';
import { PhotoAspectRatio } from '@w3lcome/types/dist/models/enums/photo-aspect-radio.enum';
import MeetingDetailsModal from '_/components/MeetingDetailsModal';
import { useAuth } from '_/hooks/AuthContext';
import { useCustomization } from '_/hooks/CustomizationContext';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';
import { CustomCameraPicture } from '_/interfaces/VisitsModelCheckin';
import LivenessMask from '_/screens/Checkin/Picture/PictureScreen/LivenessMask';
import {
  faceValidationApi,
  ipadLogsApi,
  serproValidTokenApi,
  visitsValidationsApi,
} from '_/services/api';
import { useMediaPipeStore } from '_/store/useMediaPipeStore';
import axios from 'axios';
import { CameraStatus } from '..';
import BottomModalContainer from '../BottomModalContainer';
import FaceScanner from './FaceScanner';

import { createLivenessValidator } from 'liveness-validator';
import { calculateSmartCrop, detectAndCropFace } from './detectAndCropFace';

const describeVideo = (video: HTMLVideoElement | null) =>
  video
    ? `readyState=${video.readyState}; videoWidth=${video.videoWidth}; videoHeight=${
        video.videoHeight
      }; paused=${video.paused}; src=${
        video.srcObject ? 'mediaStream' : video.currentSrc || 'none'
      }`
    : 'video=null';

const LANDMARKER_INIT_GRACE_MS = 6000;

const PictureScreenWeb: React.FC = () => {
  const { t } = useTranslation();
  const { visit, setVisit, resetData } = useVisit();
  const navigation = useNavigation<CheckinProps>();
  const { goToNextScreen } = useNextCheckinScreen(AppRoute.CHECKIN_PICTURE);
  const { width, height } = useWindowDimensions();
  const { hasFaceDetectionEnabled, selectedOrFallbackVisitFlowType } =
    useCentralizedValuesFallback();

  const { ipad } = useAuth();
  const { customization, company } = useCustomization();

  const [startCameraState, setStartCameraState] = useState(CameraStatus.IDLE);
  const [isStartPicture, setIsStartPicture] = useState(false);
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [serproValidationLoading, setSerproValidationLoading] = useState(false);
  const [serproShowDetails, setSerproShowDetails] = useState(false);

  const [isCapturingVisualState, setIsCapturingVisualState] = useState(false);

  const [countdown, setCountdown] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isValidatingUpload, setIsValidatingUpload] = useState(false);
  const lastTimestampRef = useRef(0);
  const [feedback, setFeedback] = useState<string>(t('feedback.alignFace'));
  const [permission, requestPermission] = useCameraPermissions();

  const [landmarkerInitFailed, setLandmarkerInitFailed] = useState(false);
  const initErrorLoggedRef = useRef(false);
  const startCountdownRef = useRef<() => void>(() => {});
  const landmarkerInitFailedRef = useRef<boolean>(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const awsFailedAttemptsRef = useRef(0);

  const {
    secondsToTakePicture: initialCountdownValue = 3,
    showPhotoInstructionsKiosk = null,
    isFaceLivenessEnabled,
    faceValidationMinConfidence,
    faceValidationMinSharpness,
    faceValidationMinBrightness,
    allowPhotoUpload,
  } = company?.visitsConfig ?? {};

  const useIpadTypeConfig = !!ipad?.photoConfigOverrideEnabled;

  const faceLiveness = useIpadTypeConfig
    ? ipad?.kioskFaceLivenessEnabled ?? false
    : isFaceLivenessEnabled ?? false;

  const secondsToTakePicture = useIpadTypeConfig
    ? ipad?.secondsToTakePicture ?? 3
    : initialCountdownValue ?? 3;

  const photoAspectRatio = useIpadTypeConfig
    ? ipad?.photoAspectRatio
    : company?.visitsConfig?.photoAspectRatio;

  const cropConfig = useIpadTypeConfig
    ? {
        cropExpandTop: ipad?.cropExpandTop ?? 0.6,
        cropExpandBottom: ipad?.cropExpandBottom ?? 0.5,
        cropExpandSide: ipad?.cropExpandSide ?? 0.5,
      }
    : {
        cropExpandTop: company?.visitsConfig?.cropExpandTop ?? 0.6,
        cropExpandBottom: company?.visitsConfig?.cropExpandBottom ?? 0.5,
        cropExpandSide: company?.visitsConfig?.cropExpandSide ?? 0.5,
      };

  const minConfidence = useIpadTypeConfig
    ? ipad.faceValidationMinConfidence
    : faceValidationMinConfidence;

  const minSharpness = useIpadTypeConfig
    ? ipad.faceValidationMinSharpness
    : faceValidationMinSharpness;

  const minBrightness = useIpadTypeConfig
    ? ipad.faceValidationMinBrightness
    : faceValidationMinBrightness;

  const photoUpload = useIpadTypeConfig ? ipad.allowPhotoUpload : allowPhotoUpload;

  const pictureCanSkip = useIpadTypeConfig
    ? ipad.pictureCanSkip
    : selectedOrFallbackVisitFlowType?.pictureCanSkip;

  const serproValidationEnabled = selectedOrFallbackVisitFlowType?.serproValidationEnabled;
  const serproCoHostValidationEnabled =
    selectedOrFallbackVisitFlowType?.serproCoHostValidationEnabled;

  const animatedLabel = useRef(new Animated.Value(0)).current;
  const cameraBoxViewRef = useRef<View>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { landmarker, mediaPipeInitError } = useMediaPipeStore((s) => ({
    landmarker: s.landmarker,
    mediaPipeInitError: s.error,
  }));
  const isProcessingRef = useRef(false);
  const isCapturedRef = useRef(false);

  const countdownTimerRef = useRef<
    ReturnType<typeof setTimeout> | ReturnType<typeof setInterval> | null
  >(null);
  const animationFrameRef = useRef<number | null>(null);

  const faceRef = useRef<NormalizedLandmark[] | null>(null);

  const validatorRef = useRef(
    createLivenessValidator({ mirrored: false, faceSize: { min: 0.18, max: 0.4 } })
  );

  const lastDetectTickRef = useRef<number>(0);
  const watchdogIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loopStallLoggedRef = useRef(false);
  const hadFaceRef = useRef(false);
  const faceLostAtRef = useRef<number | null>(null);
  const sessionStatsRef = useRef<{
    startedAt: number;
    frames: number;
    framesWithFace: number;
    faceLostCount: number;
  } | null>(null);
  const lastLogAtByTypeRef = useRef<Record<string, number>>({});

  const logMediaPipe = useCallback((type: string, message: string, throttleMs = 60000) => {
    const now = Date.now();
    const last = lastLogAtByTypeRef.current[type] ?? 0;
    if (throttleMs > 0 && now - last < throttleMs) return;
    lastLogAtByTypeRef.current[type] = now;
    try {
      Promise.resolve(ipadLogsApi.insert({ type, message })).catch(() => {});
    } catch {}
  }, []);

  const flushSessionSummary = useCallback(
    (reason: string) => {
      const s = sessionStatsRef.current;
      sessionStatsRef.current = null;
      if (!s || s.frames === 0) return;

      const durationMs = Date.now() - s.startedAt;
      const avgFps = durationMs > 0 ? (s.frames / durationMs) * 1000 : 0;
      const facePct = (s.framesWithFace / s.frames) * 100;

      const isAnomalous = facePct < 80 || avgFps < 20 || s.faceLostCount >= 2;
      if (!isAnomalous) return;

      logMediaPipe(
        '[MediaPipe] session summary',
        `reason=${reason}; durationMs=${durationMs}; frames=${s.frames}; framesWithFace=${
          s.framesWithFace
        } (${facePct.toFixed(1)}%); avgFps=${avgFps.toFixed(1)}; faceLostEvents=${
          s.faceLostCount
        }; ${describeVideo(videoRef.current)}`,
        0
      );
    },
    [logMediaPipe]
  );

  const stopDetectionWatchdog = useCallback(() => {
    if (watchdogIntervalRef.current) {
      clearInterval(watchdogIntervalRef.current);
      watchdogIntervalRef.current = null;
    }
  }, []);

  const startDetectionWatchdog = useCallback(() => {
    stopDetectionWatchdog();
    loopStallLoggedRef.current = false;
    watchdogIntervalRef.current = setInterval(() => {
      if (isCapturedRef.current || loopStallLoggedRef.current) return;
      const sinceLastTick = Date.now() - lastDetectTickRef.current;
      if (lastDetectTickRef.current > 0 && sinceLastTick > 5000) {
        loopStallLoggedRef.current = true;
        const landmarkerNow = useMediaPipeStore.getState().landmarker;
        logMediaPipe(
          '[MediaPipe] detection loop stalled',
          `Loop de detecção sem tick há ${sinceLastTick}ms com câmera aberta. landmarker=${
            landmarkerNow ? 'ok' : 'null'
          }; ${describeVideo(videoRef.current)}`,
          0
        );
      }
    }, 5000);
  }, [logMediaPipe, stopDetectionWatchdog]);

  const toggleLabel = useCallback(
    (isVisible: boolean) => {
      Animated.timing(animatedLabel, {
        toValue: isVisible ? 1 : 0,
        duration: 600,
        useNativeDriver: true,
        easing: Easing.inOut(Easing.cubic),
      }).start();
    },
    [animatedLabel]
  );

  const stopCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current as any);
      clearTimeout(countdownTimerRef.current as any);
      countdownTimerRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    setCountdown(null);
  }, [countdownTimerRef]);

  const resetStates = useCallback(() => {
    toggleLabel(false);
    stopCountdown();
    stopDetectionWatchdog();
    flushSessionSummary('sessionEnd');
  }, [toggleLabel, stopCountdown, stopDetectionWatchdog, flushSessionSummary]);

  const aspectRatiosMap: Record<PhotoAspectRatio, number> = {
    [PhotoAspectRatio.THREE_BY_FOUR]: 3 / 4,
    [PhotoAspectRatio.FOUR_BY_THREE]: 4 / 3,
    [PhotoAspectRatio.ONE_BY_ONE]: 1 / 1,
  };

  const [step, setStep] = useState<
    'INSTRUCTION_INITIAL' | 'COHOST_PICTURE' | 'VISITOR_PICTURE' | 'COHOST_VALIDATION_SUCCESS'
  >(() =>
    showPhotoInstructionsKiosk || photoUpload
      ? 'INSTRUCTION_INITIAL'
      : serproCoHostValidationEnabled
      ? 'COHOST_PICTURE'
      : 'VISITOR_PICTURE'
  );

  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current as any);
        clearTimeout(countdownTimerRef.current as any);
      }
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (watchdogIntervalRef.current) clearInterval(watchdogIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    const run = async () => {
      if (step === 'COHOST_PICTURE') {
        if (!visit.coHostPicture) {
          startCamera();
          return;
        }
        await serproValidation(visit?.coHostPicture?.serproBase64, true);
        return;
      }

      if (step === 'VISITOR_PICTURE') {
        if (!visit.picture) {
          startCamera(true);
          return;
        }
      }
    };

    run();
  }, [step, visit.coHostPicture, visit.picture]);

  useEffect(() => {
    if (!isStartPicture) {
      initErrorLoggedRef.current = false;
      landmarkerInitFailedRef.current = false;
      setLandmarkerInitFailed(false);
      awsFailedAttemptsRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }
    if (!hasFaceDetectionEnabled || landmarker) return;

    if (mediaPipeInitError && !initErrorLoggedRef.current) {
      initErrorLoggedRef.current = true;
      logMediaPipe('[MediaPipe] engine init error', mediaPipeInitError, 0);
      setLandmarkerInitFailed(true);
      return;
    }

    const timer = setTimeout(() => {
      if (!landmarker && !initErrorLoggedRef.current) {
        initErrorLoggedRef.current = true;
        logMediaPipe(
          '[MediaPipe] engine init error',
          mediaPipeInitError ?? `landmarker not ready after ${LANDMARKER_INIT_GRACE_MS}ms`,
          0
        );
        setLandmarkerInitFailed(true);
      }
    }, LANDMARKER_INIT_GRACE_MS);

    return () => clearTimeout(timer);
  }, [isStartPicture, landmarker, hasFaceDetectionEnabled, mediaPipeInitError, logMediaPipe]);

  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  if (typeof document !== 'undefined' && !offscreenCanvasRef.current) {
    offscreenCanvasRef.current = document.createElement('canvas');
  }

  const mirrorCanvasRef = useRef<HTMLCanvasElement | null>(null);
  if (typeof document !== 'undefined' && !mirrorCanvasRef.current) {
    mirrorCanvasRef.current = document.createElement('canvas');
  }

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setUploadError(null);
      const file = event.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;

        if (!hasFaceDetectionEnabled) {
          setVisit({
            picture: {
              uri: base64,
              width: 600,
              height: 800,
              base64: base64.split(',')[1],
              serproBase64: null,
            },
          });
          return;
        }

        try {
          setIsValidatingUpload(true);
          const response = await faceValidationApi.insert({
            image: base64,
            config: { minConfidence, minSharpness, minBrightness },
          });
          setIsValidatingUpload(false);

          if (!response.valid) {
            setUploadError(t(response.reason));
            return;
          }

          setVisit({
            picture: {
              uri: base64,
              width: 600,
              height: 800,
              base64: base64.split(',')[1],
              serproBase64: null,
            },
          });
        } catch {
          setIsValidatingUpload(false);
          setUploadError(t('invalidPhotoFacial'));
        }
      };
      reader.readAsDataURL(file);
    },
    [hasFaceDetectionEnabled, minConfidence, minSharpness, minBrightness, setVisit, t]
  );

  const startVideoValidation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    flushSessionSummary('restart');
    sessionStatsRef.current = {
      startedAt: Date.now(),
      frames: 0,
      framesWithFace: 0,
      faceLostCount: 0,
    };
    hadFaceRef.current = false;
    faceLostAtRef.current = null;
    lastDetectTickRef.current = Date.now();
    startDetectionWatchdog();

    const detect = async () => {
      lastDetectTickRef.current = Date.now();
      if (isCapturedRef.current || !landmarker || landmarkerInitFailed) return;

      if (!videoRef.current) videoRef.current = document.querySelector('video');
      const video = videoRef.current;

      if (video && video.readyState >= 2 && !isProcessingRef.current) {
        isProcessingRef.current = true;
        try {
          const now = performance.now();
          const timestamp = Math.max(now, lastTimestampRef.current + 1);
          lastTimestampRef.current = timestamp;

          const results = landmarker.detectForVideo(video, timestamp);
          if (sessionStatsRef.current) sessionStatsRef.current.frames++;
          if (results.faceLandmarks?.length > 0) {
            if (sessionStatsRef.current) sessionStatsRef.current.framesWithFace++;
            hadFaceRef.current = true;
            faceLostAtRef.current = null;
            const landmarks = results.faceLandmarks[0];
            faceRef.current = landmarks;

            const liveness = validatorRef.current.validate(landmarks, faceLiveness);

            if (!isFeedbackLockedRef.current) {
              setIsFaceDetected(liveness.isValid);
              setFeedback(t(liveness.feedback));
            }

            if (faceLiveness && liveness.isValid && liveness.feedback === 'face.readyToCapture') {
              if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
              }
              stopCountdown();

              captureFrame(faceRef);
              return;
            }

            if (liveness.isValid && !countdownTimerRef.current) {
              startCountdown();
            } else if (!liveness.isValid) {
              stopCountdown();
            }
          } else {
            if (hadFaceRef.current) {
              if (faceLostAtRef.current === null) {
                faceLostAtRef.current = Date.now();
              } else if (Date.now() - faceLostAtRef.current > 2000) {
                hadFaceRef.current = false;
                faceLostAtRef.current = null;
                const s = sessionStatsRef.current;
                if (s) s.faceLostCount++;
                if (!s || s.faceLostCount <= 3) {
                  logMediaPipe(
                    '[MediaPipe] face points lost',
                    `Pontos do rosto perdidos por >2000ms durante sessão ativa. ${describeVideo(
                      video
                    )}`,
                    0
                  );
                }
              }
            }
            setIsFaceDetected(false);
            setFeedback(t('feedback.noFaceDetected'));
            stopCountdown();
          }
        } catch (err) {
          logMediaPipe(
            '[MediaPipe] detection error',
            `Erro no loop de detecção: ${
              err instanceof Error ? err.message : String(err)
            }. ${describeVideo(video)}`,
            0
          );
          setLandmarkerInitFailed(true);
          Promise.resolve(useMediaPipeStore.getState().reinitialize('detection error')).catch(
            () => {}
          );
          return;
        } finally {
          isProcessingRef.current = false;
        }
      }

      animationFrameRef.current = requestAnimationFrame(detect);
    };

    animationFrameRef.current = requestAnimationFrame(detect);
  }, [landmarker, faceLiveness, t, logMediaPipe, flushSessionSummary, startDetectionWatchdog]);

  const prevLandmarkerRef = useRef(landmarker);

  useEffect(() => {
    if (prevLandmarkerRef.current === landmarker) return;
    prevLandmarkerRef.current = landmarker;
    if (!landmarker) return;
    if (!isStartPicture || isCapturedRef.current || !hasFaceDetectionEnabled) return;
    if (landmarkerInitFailed) return;

    logMediaPipe(
      '[MediaPipe] landmarker recreated',
      'FaceLandmarker recriado com sessão de câmera ativa — reiniciando loop de detecção',
      0
    );
    startVideoValidation();
  }, [
    landmarker,
    isStartPicture,
    hasFaceDetectionEnabled,
    landmarkerInitFailed,
    startVideoValidation,
    logMediaPipe,
  ]);

  const setFacialError = useCallback(
    (msg: string, ms = 3000) => {
      isFeedbackLockedRef.current = true;
      setFeedback(t(msg));
      setIsFaceDetected(false);
      setIsCapturingVisualState(false);
      validatorRef.current.reset();
      setTimeout(() => {
        isFeedbackLockedRef.current = false;
      }, ms);
    },
    [t]
  );

  const isFeedbackLockedRef = useRef(false);

  const generateImg = useCallback(
    (
      coords: { cropX: number; cropY: number; cropWidth: number; cropHeight: number },
      targetW: number,
      targetH: number,
      quality: number,
      mirror = false
    ): { uri: string; base64: string } | null => {
      const video = videoRef.current;
      const offscreenCanvas = offscreenCanvasRef.current;
      const mirrorCanvas = mirrorCanvasRef.current;
      if (!video || !offscreenCanvas || !mirrorCanvas) return null;

      const ctx = offscreenCanvas.getContext('2d', { alpha: false });
      if (!ctx) return null;

      offscreenCanvas.width = targetW;
      offscreenCanvas.height = targetH;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        video,
        coords.cropX,
        coords.cropY,
        coords.cropWidth,
        coords.cropHeight,
        0,
        0,
        targetW,
        targetH
      );

      const base64Full = offscreenCanvas.toDataURL('image/jpeg', quality);

      if (!mirror) {
        return { uri: base64Full, base64: base64Full.split(',')[1] };
      }

      mirrorCanvas.width = targetW;
      mirrorCanvas.height = targetH;
      const mCtx = mirrorCanvas.getContext('2d', { alpha: false });
      if (!mCtx) return { uri: base64Full, base64: base64Full.split(',')[1] };

      mCtx.save();
      mCtx.translate(targetW, 0);
      mCtx.scale(-1, 1);
      mCtx.drawImage(offscreenCanvas, 0, 0);
      mCtx.restore();

      return {
        uri: mirrorCanvas.toDataURL('image/jpeg', quality),
        base64: base64Full.split(',')[1],
      };
    },
    [videoRef, offscreenCanvasRef, mirrorCanvasRef]
  );

  const savePictureAndFinish = useCallback(
    (
      dbResult: { uri: string; base64: string },
      serproResult: { uri: string; base64: string } | null
    ) => {
      const visitKey = step === 'COHOST_PICTURE' ? 'coHostPicture' : 'picture';
      const dbRatio =
        (photoAspectRatio ? aspectRatiosMap[photoAspectRatio as PhotoAspectRatio] : null) ?? 3 / 4;
      const dbW = 600;
      const dbH = Math.round(dbW / dbRatio);

      setVisit({
        [visitKey]: {
          uri: dbResult.uri,
          width: dbW,
          height: dbH,
          base64: dbResult.base64,
          serproBase64: serproResult?.base64 ?? null,
        },
      });
      setIsStartPicture(false);
      setIsCapturingVisualState(false);
      resetStates();
      awsFailedAttemptsRef.current = 0;
    },
    [step, photoAspectRatio, aspectRatiosMap, resetStates, setVisit]
  );

  const bypassFaceValidation = useCallback(
    (
      vW: number,
      vH: number,
      dbRatio: number,
      dbW: number,
      dbH: number,
      isSerproActive: boolean
    ) => {
      const getCentralCrop = (ratio: number) => {
        if (vW / vH > ratio) {
          const sw = vH * ratio;
          return { cropX: (vW - sw) / 2, cropY: 0, cropWidth: sw, cropHeight: vH };
        }
        const sh = vW / ratio;
        return { cropX: 0, cropY: (vH - sh) / 2, cropWidth: vW, cropHeight: sh };
      };

      const dbCoords = getCentralCrop(dbRatio);
      const dbResult = generateImg(dbCoords, dbW, dbH, 0.85, true);

      if (dbResult) {
        const serproResult = isSerproActive
          ? generateImg(getCentralCrop(3 / 4), 960, 1280, 0.95, false)
          : null;

        savePictureAndFinish(dbResult, serproResult);
        return true;
      }
      return false;
    },
    [generateImg, savePictureAndFinish]
  );

  const resetOnError = useCallback(
    (msg: string, retry = false) => {
      isCapturedRef.current = false;
      setIsCapturingVisualState(false);
      setFacialError(msg);
      if (retry) {
        if (landmarkerInitFailedRef.current) {
          retryTimeoutRef.current = setTimeout(() => {
            setIsFaceDetected(true);
            startCountdownRef.current();
          }, 3000);
        } else {
          if (!faceLiveness) setTimeout(() => startVideoValidation(), 3000);
          else startVideoValidation();
        }
      }
    },
    [faceLiveness, startVideoValidation, setFacialError]
  );

  const captureFallbackNoLandmarks = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    const { videoWidth: vW, videoHeight: vH } = video;
    const isSerproActive = serproValidationEnabled || serproCoHostValidationEnabled;
    const dbRatio =
      (photoAspectRatio ? aspectRatiosMap[photoAspectRatio as PhotoAspectRatio] : null) ?? 3 / 4;
    const dbW = 600;
    const dbH = Math.round(dbW / dbRatio);

    const getCentralCrop = (ratio: number) => {
      if (vW / vH > ratio) {
        const sw = vH * ratio;
        return { cropX: (vW - sw) / 2, cropY: 0, cropWidth: sw, cropHeight: vH };
      }
      const sh = vW / ratio;
      return { cropX: 0, cropY: (vH - sh) / 2, cropWidth: vW, cropHeight: sh };
    };

    const maxDimension = 800;
    let targetW = vW;
    let targetH = vH;
    if (vW > maxDimension || vH > maxDimension) {
      if (vW > vH) {
        targetW = maxDimension;
        targetH = Math.round((vH * maxDimension) / vW);
      } else {
        targetH = maxDimension;
        targetW = Math.round((vW * maxDimension) / vH);
      }
    }

    try {
      const fullCoords = { cropX: 0, cropY: 0, cropWidth: vW, cropHeight: vH };
      const tempFullImg = generateImg(fullCoords, targetW, targetH, 0.8, true);
      if (!tempFullImg) return resetOnError('invalidPhotoFacial');

      const response = await faceValidationApi.insert({
        image: `data:image/jpeg;base64,${tempFullImg.base64}`,
        config: {
          minConfidence,
          minSharpness: Math.min(Number(minSharpness), 15),
          minBrightness,
        },
      });

      if (!response.valid) return resetOnError(response.reason, true);

      const box = response.face?.BoundingBox;

      const dbCoords = box
        ? calculateSmartCrop(
            { x: box.Left, y: box.Top, width: box.Width, height: box.Height },
            vW,
            vH,
            dbRatio,
            cropConfig
          )
        : getCentralCrop(dbRatio);
      const dbResult = generateImg(dbCoords, dbW, dbH, 0.85, true);
      if (!dbResult) return resetOnError('invalidPhotoFacial');

      const serproResult = isSerproActive
        ? generateImg(
            box
              ? calculateSmartCrop(
                  { x: box.Left, y: box.Top, width: box.Width, height: box.Height },
                  vW,
                  vH,
                  3 / 4,
                  cropConfig
                )
              : getCentralCrop(3 / 4),
            960,
            1280,
            0.95,
            false
          )
        : null;

      savePictureAndFinish(dbResult, serproResult);
    } catch (err) {
      console.error('[Fallback Capture] Erro na validação facial:', err);
      awsFailedAttemptsRef.current += 1;

      if (awsFailedAttemptsRef.current >= 3) {
        const bypassed = bypassFaceValidation(vW, vH, dbRatio, dbW, dbH, !!isSerproActive);
        if (bypassed) return;
      }

      return resetOnError('apiValiationError', true);
    }
  }, [
    photoAspectRatio,
    serproValidationEnabled,
    serproCoHostValidationEnabled,
    step,
    generateImg,
    resetOnError,
    faceValidationApi,
    minConfidence,
    minSharpness,
    minBrightness,
    cropConfig,
    savePictureAndFinish,
    bypassFaceValidation,
  ]);

  const captureFrame = useCallback(
    async (faceRef: React.RefObject<NormalizedLandmark[] | null>) => {
      const video = videoRef.current;
      if (!video) return;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      if (video.readyState < 2) {
        setTimeout(() => captureFrame(faceRef), 200);
        return;
      }

      if (isCapturedRef.current) return;

      isCapturedRef.current = true;
      setIsCapturingVisualState(true);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      try {
        const { videoWidth: vW, videoHeight: vH } = video;
        const isSerproActive = serproValidationEnabled || serproCoHostValidationEnabled;
        const dbRatio =
          (photoAspectRatio ? aspectRatiosMap[photoAspectRatio as PhotoAspectRatio] : null) ??
          3 / 4;
        const dbW = 600;
        const dbH = Math.round(dbW / dbRatio);

        const getCentralCrop = (ratio: number) => {
          if (vW / vH > ratio) {
            const sw = vH * ratio;
            return { cropX: (vW - sw) / 2, cropY: 0, cropWidth: sw, cropHeight: vH };
          }
          const sh = vW / ratio;
          return { cropX: 0, cropY: (vH - sh) / 2, cropWidth: vW, cropHeight: sh };
        };

        if (!landmarker || landmarkerInitFailed) {
          logMediaPipe(
            '[MediaPipe] capturing without landmarks',
            `landmarker indisponível no momento da captura; enviando foto para validação no AWS sem crop inteligente. ${describeVideo(
              video
            )}`,
            0
          );
          return captureFallbackNoLandmarks();
        }

        if (!hasFaceDetectionEnabled) {
          const cropResults = detectAndCropFace({
            landmarker,
            video,
            lastTimestampRef,
            faceRef,
            vW,
            vH,
            dbRatio,
            cropConfig,
            generateImg,
            dbW,
            dbH,
            isSerproActive,
            getCentralCrop,
            logMediaPipe,
            describeVideo,
            captureFallbackNoLandmarks,
            resetOnError,
          });

          if (!cropResults) return;

          savePictureAndFinish(cropResults.dbResult, cropResults.serproResult);
          return;
        }

        for (let i = 0; i < 3; i++) {
          const sampleResults = landmarker.detectForVideo(
            video,
            Math.max(performance.now(), lastTimestampRef.current + 1)
          );
          lastTimestampRef.current = Math.max(performance.now(), lastTimestampRef.current + 1);
          const sampleLandmarks = sampleResults.faceLandmarks?.[0];

          if (!sampleLandmarks) {
            logMediaPipe(
              '[MediaPipe] landmarks lost during capture sampling',
              `attempt=${i}; ${describeVideo(video)}`,
              0
            );
            return captureFallbackNoLandmarks();
          }

          const sampleValidation = validatorRef.current.validate(sampleLandmarks, faceLiveness);
          if (!sampleValidation.isValid) {
            return resetOnError(sampleValidation.feedback, true);
          }

          await new Promise((resolve) => setTimeout(resolve, 40));
        }

        const cropResults = detectAndCropFace({
          landmarker,
          video,
          lastTimestampRef,
          faceRef,
          vW,
          vH,
          dbRatio,
          cropConfig,
          generateImg,
          dbW,
          dbH,
          isSerproActive,
          getCentralCrop,
          logMediaPipe,
          describeVideo,
          captureFallbackNoLandmarks,
          resetOnError,
          resetOnErrorRetry: true,
        });

        if (!cropResults) return;

        const { dbResult, serproResult } = cropResults;

        try {
          const response = await faceValidationApi.insert({
            image: `data:image/jpeg;base64,${dbResult.base64}`,
            config: { minConfidence, minSharpness, minBrightness },
          });

          if (!response.valid) return resetOnError(response.reason, true);
        } catch {
          awsFailedAttemptsRef.current += 1;
          if (awsFailedAttemptsRef.current >= 3) {
            const bypassed = bypassFaceValidation(vW, vH, dbRatio, dbW, dbH, !!isSerproActive);
            if (bypassed) return;
          }
          return resetOnError('apiValiationError', true);
        }

        savePictureAndFinish(dbResult, serproResult);
      } catch (err) {
        console.error('Erro na captura:', err);
        logMediaPipe(
          '[MediaPipe] capture error',
          `Erro durante captura/validação do frame: ${
            err instanceof Error ? err.message : String(err)
          }. ${describeVideo(videoRef.current)}`,
          0
        );
        isCapturedRef.current = false;
        setLandmarkerInitFailed(true);
        landmarkerInitFailedRef.current = true;
        captureFallbackNoLandmarks();
        Promise.resolve(useMediaPipeStore.getState().reinitialize('capture error')).catch(() => {});
      }
    },
    [
      serproValidationEnabled,
      serproCoHostValidationEnabled,
      step,
      photoAspectRatio,
      landmarker,
      landmarkerInitFailed,
      faceLiveness,
      hasFaceDetectionEnabled,
      startVideoValidation,
      cropConfig,
      minConfidence,
      minSharpness,
      minBrightness,
      logMediaPipe,
      generateImg,
      resetOnError,
      captureFallbackNoLandmarks,
      savePictureAndFinish,
      bypassFaceValidation,
    ]
  );

  const resetCameraState = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    stopDetectionWatchdog();
    flushSessionSummary('resetCameraState');
    isCapturedRef.current = false;
    isProcessingRef.current = false;
    setIsCapturingVisualState(false);
    faceRef.current = null;
    videoRef.current = null;
    setIsFaceDetected(false);
    setFeedback(t('feedback.alignFace'));
    awsFailedAttemptsRef.current = 0;
    stopCountdown();
  }, [t, stopDetectionWatchdog, flushSessionSummary]);

  const startCountdown = () => {
    if (countdownTimerRef.current) return;

    if (!hasFaceDetectionEnabled || landmarkerInitFailedRef.current) {
      let count = secondsToTakePicture <= 0 ? 3 : secondsToTakePicture;
      setCountdown(count);

      countdownTimerRef.current = setInterval(() => {
        count -= 1;
        setCountdown(count);

        if (count <= 0) {
          clearInterval(countdownTimerRef.current as any);
          countdownTimerRef.current = null;
          setCountdown(null);
          captureFrame(faceRef);
        }
      }, 1000);
      return;
    }

    const delayBackgroundMs = faceLiveness ? 300 : 1400;

    countdownTimerRef.current = setTimeout(() => {
      countdownTimerRef.current = null;
      captureFrame(faceRef);
    }, delayBackgroundMs);
  };

  async function serproValidation(imagemBase64: string | undefined | null, isCoHost = false) {
    setSerproValidationLoading(true);
    if (!imagemBase64) return;

    try {
      const validatedToken: any = await serproValidTokenApi.getList({
        companyId: visit.companyId ?? selectedOrFallbackVisitFlowType?.companyId,
        $limit: 1,
      });

      if (!validatedToken.token) {
        throw new Error(`TOKEN_NOT_AVAILABLE`);
      }

      const response = await axios.post(
        'https://gateway.apiserpro.serpro.gov.br/datavalid/v4/pf-facial',
        {
          cpf: step === 'COHOST_PICTURE' ? visit.coHost?.host?.cpf : visit.cpf,
          validacao: {
            nome: step === 'COHOST_PICTURE' ? visit.coHost?.host?.name : visit.name,
            situacao_cpf: 'regular',
            nacionalidade: 1,
            biometria_facial: {
              formato: 'JPG',
              base64: imagemBase64,
            },
          },
        },
        {
          headers: {
            Authorization: `Bearer ${validatedToken.token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSerproValidationLoading(false);
      const similarity = Number(response.data?.biometria_facial?.similaridade);

      await visitsValidationsApi.insert({
        cpf: visit.cpf?.replace(/\D/g, ''),
        companyId: visit.companyId ?? selectedOrFallbackVisitFlowType?.companyId,
        validationType: ValidationType.SERPRO_FACIAL,
        status: 'success',
        notes: `similaridade=${similarity};`,
        pictureBase64: imagemBase64,
      });

      if (similarity > 0.9) {
        if (!isCoHost) {
          setSerproShowDetails(true);
        } else {
          setStep('COHOST_VALIDATION_SUCCESS');
        }
      } else {
        await ipadLogsApi.insert({
          type: '[Serpro Validation]',
          message: `CPF: ${visit.cpf?.replace(/\D/g, '')} - Nome: ${
            visit.name
          } - biometria_facial: ${JSON.stringify(response.data)}`,
        });
        navigation.replace(AppRoute.CHECKIN_ERROR_SCREEN);
      }
    } catch (error: any) {
      await visitsValidationsApi.insert({
        cpf: visit.cpf?.replace(/\D/g, ''),
        companyId: visit.companyId ?? selectedOrFallbackVisitFlowType?.companyId,
        validationType: ValidationType.SERPRO_FACIAL,
        status: 'error',
        errorMessage: error?.message ?? 'UNKNOWN_ERROR',
        pictureBase64: imagemBase64,
      });

      await ipadLogsApi.insert({
        type: '[Serpro Validation] - Error',
        message:
          error?.status === 422
            ? `CPF: ${visit.cpf?.replace(/\D/g, '')} - Nome: ${
                visit.name
              } - Error: Não atendem aos requisitos mínimos para validação.`
            : `CPF: ${visit.cpf?.replace(/\D/g, '')} - Nome: ${
                visit.name
              } - Error: ${JSON.stringify(error)}`,
      });
      setSerproValidationLoading(false);
      navigation.replace(AppRoute.CHECKIN_ERROR_SCREEN);
    }
  }

  const startCamera = async (startPicture = false) => {
    resetCameraState();
    validatorRef.current.reset();
    if (visit.picture || permission?.status === 'denied') return;
    if (permission?.status !== 'granted') {
      const result = await requestPermission();
      if (result.status !== 'granted') {
        setStartCameraState(CameraStatus.PERMISSION_PENDING);
        showMessage({
          message: t('error'),
          description: t('pictureScreen.CameraPermissionDenied'),
          backgroundColor: colors.danger,
        });
        return;
      }
    }
    setFeedback(t('feedback.alignFace'));
    setStartCameraState(CameraStatus.READY_TO_TAKE_PICTURE);
    toggleLabel(true);
    setIsFaceDetected(false);
    if (!showPhotoInstructionsKiosk || startPicture) {
      setTimeout(() => setIsStartPicture(true), 1000);
    }
  };

  useEffect(() => {
    startCountdownRef.current = startCountdown;
  }, [startCountdown]);

  const PicturePreview = ({ photo }: { photo: CustomCameraPicture }) => {
    return (
      <View style={styles.previewContainer}>
        <View style={styles.previewInner}>
          <Image source={{ uri: photo.uri }} style={styles.fullImage} resizeMode="contain" />
        </View>
        {serproShowDetails && (
          <MeetingDetailsModal
            isModalVisible={serproShowDetails}
            handleConfirm={goToNextScreen}
            hideModalMeeting={() => {
              resetData();
              navigation.reset({ index: 0, routes: [{ name: AppRoute.HOME }] });
            }}
          />
        )}
      </View>
    );
  };

  const renderCameraOrPreview = () => {
    if (visit.picture) {
      if (serproValidationLoading) {
        return (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <Text fontType="bold" size={fontSizes.lg}>
              Só um instante! Estamos validando seu rosto para liberar o acesso
            </Text>
            <ActivityIndicator color={colors.primary} size="large" />
          </View>
        );
      }
      return (
        <View style={styles.innerContentShadow}>
          <PicturePreview photo={visit.picture} />
        </View>
      );
    }

    if (visit.coHostPicture) {
      if (serproCoHostValidationEnabled && step === 'COHOST_PICTURE') {
        return (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 20 }}>
            <Text fontType="bold" size={fontSizes.lg}>
              Só um instante! Estamos validando o rosto do responsável
            </Text>
            <ActivityIndicator color={colors.primary} size="large" />
          </View>
        );
      }

      return (
        <View
          style={[
            styles.innerContentShadow,
            { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 40 },
          ]}
        >
          <View
            style={{
              backgroundColor: colors.success,
              borderRadius: 16,
              paddingVertical: 48,
              paddingHorizontal: 32,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.25,
              shadowRadius: 4,
              elevation: 4,
            }}
          >
            <Text
              style={{
                fontSize: 30,
                fontWeight: '700',
                color: colors.white,
                textAlign: 'center',
                marginBottom: 12,
              }}
            >
              {t('Validação do responsável realizada com sucesso!')}
            </Text>
            <Text
              style={{
                fontSize: 20,
                fontWeight: '400',
                color: colors.white,
                textAlign: 'center',
                opacity: 0.9,
                maxWidth: 600,
              }}
            >
              {t('Agora prossiga para a validação do credenciado.')}
            </Text>
          </View>

          <Button
            backgroundColor={customization?.mainColor || colors.primary}
            labelColor={colors.white}
            buttonStyles={{
              justifyContent: 'space-between',
              height: 64,
              alignSelf: 'center',
              marginTop: 60,
            }}
            onPress={() => setStep('VISITOR_PICTURE')}
            rightIcon="arrow-right"
          >
            {t('Iniciar validação facial do credenciado')}
          </Button>
        </View>
      );
    }

    if (startCameraState === CameraStatus.PERMISSION_PENDING) {
      return (
        <View style={styles.innerContentShadow}>
          <View style={{ flex: 1 }}>
            <View style={styles.center}>
              <Text size={fontSizes.xl} color={colors.dark2} fontType="semibold">
                {t('pictureScreen.CameraPermissionDenied')}
              </Text>
              <Text size={fontSizes.xl} color={colors.dark2} fontType="semibold">
                {t('pictureScreen.ContinueButton')}
              </Text>
            </View>
            <View style={styles.picturePlaceholderBox}>
              <View style={styles.picturePlaceholderBorder} />
              <Image
                source={
                  height >= width ? images.picturePlaceholder : images.picturePlaceholderHorizontal
                }
                style={styles.picturePlaceholder}
              />
              <View style={styles.picturePlaceholderBorder} />
            </View>
          </View>
        </View>
      );
    }

    if (step === 'INSTRUCTION_INITIAL') {
      return (
        <>
          <View
            style={{
              marginTop: 32,
              padding: 20,
              borderRadius: 16,
              backgroundColor: colors.white,
              shadowColor: '#000',
              shadowOpacity: 0.05,
              shadowRadius: 10,
              elevation: 3,
              gap: 16,
            }}
          >
            <View style={{ gap: 6 }}>
              <Text fontType="bold" size={fontSizes.lg2}>
                {serproCoHostValidationEnabled ? 'Validação facial' : t('getReadyForPhoto')}
              </Text>
              <Text fontType="semibold" size={fontSizes.md2} color={colors.primary}>
                Siga as orientações abaixo para garantir uma validação rápida e segura
              </Text>
            </View>

            {serproCoHostValidationEnabled && (
              <View
                style={{ backgroundColor: colors.light1, padding: 12, borderRadius: 10, gap: 4 }}
              >
                <Text fontType="semibold" size={fontSizes.sm}>
                  1. Validaremos o rosto do responsável
                </Text>
                <Text fontType="semibold" size={fontSizes.sm}>
                  2. Depois, o rosto do credenciado
                </Text>
              </View>
            )}

            <View style={{ gap: 10 }}>
              {[
                'Centralize o rosto e olhe diretamente para a câmera',
                'Mantenha expressão neutra (sem sorrir)',
                'Remova óculos, boné ou acessórios',
                'Evite se movimentar durante a foto',
              ].map((item, index) => (
                <View key={index} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <View
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      backgroundColor: customization?.mainColor || colors.primary,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text style={{ color: colors.white, fontSize: 14 }}>✓</Text>
                  </View>
                  <Text fontType="semibold" size={fontSizes.md2} style={{ flex: 1 }}>
                    {item}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          <Button
            backgroundColor={customization?.mainColor || colors.primary}
            labelColor={colors.white}
            buttonStyles={{
              justifyContent: 'space-between',
              height: 64,
              alignSelf: 'center',
              marginTop: 40,
              borderRadius: 12,
              width: 380,
            }}
            onPress={() => {
              setIsStartPicture(true);
              setStep(serproCoHostValidationEnabled ? 'COHOST_PICTURE' : 'VISITOR_PICTURE');
            }}
            disabled={isValidatingUpload}
            rightIcon="arrow-right"
          >
            {serproCoHostValidationEnabled
              ? 'Iniciar validação facial do responsável'
              : t('startFacialRegistrationProcess')}
          </Button>

          {pictureCanSkip && (
            <Button
              outline
              buttonStyles={{
                height: 64,
                alignSelf: 'center',
                marginTop: 12,
                borderRadius: 12,
                width: 380,
              }}
              onPress={() => {
                setVisit({ picture: null });
                goToNextScreen();
              }}
            >
              {t('pictureScreen.SkipPicture')}
            </Button>
          )}

          {photoUpload && !serproValidationEnabled && (
            <>
              <Button
                outline
                buttonStyles={{ height: 64, alignSelf: 'center', marginTop: 12, borderRadius: 12 }}
                onPress={() => fileInputRef.current?.click()}
                loading={isValidatingUpload}
                disabled={isValidatingUpload}
              >
                {isValidatingUpload ? t('validatingPhoto') : t('uploadPhoto')}
              </Button>

              {uploadError && (
                <View
                  style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor: '#FFF0F0',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E24B4A',
                  }}
                >
                  <Text style={{ color: '#C0392B', textAlign: 'center', fontSize: 14 }}>
                    {uploadError}
                  </Text>
                </View>
              )}
            </>
          )}
        </>
      );
    }

    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <ActivityIndicator color={colors.primary} size="large" />
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <ContainerWidthLimit>
        <Header onGoBack={() => setVisit({ picture: null, coHostPicture: null })} />
        <View style={styles.content}>{renderCameraOrPreview()}</View>

        {(!!visit.picture || !startCameraState) && (
          <BottomModalContainer
            onNextPress={async () =>
              serproValidationEnabled
                ? await serproValidation(visit.picture?.serproBase64)
                : goToNextScreen()
            }
            onRetakePress={() => {
              resetCameraState();
              setVisit({ picture: null });
              setIsStartPicture(true);
            }}
            onSkipPress={() => {
              setVisit({ picture: null });
              goToNextScreen();
            }}
            cameraAllowed={startCameraState === CameraStatus.READY_TO_TAKE_PICTURE}
            isVisible={!!visit.picture}
            canSkipPicture={pictureCanSkip}
          />
        )}

        {isStartPicture && (
          <View style={styles.overlayContainer}>
            <BlurView intensity={80} style={styles.blurContainer}>
              <View
                ref={cameraBoxViewRef}
                style={{
                  flex: 1,
                  borderRadius: 8,
                  overflow: 'hidden',
                  width: height > width ? width : 600,
                  padding: height > width ? 40 : 0,
                }}
              >
                <CameraView
                  key={isStartPicture ? 'camera-open' : 'camera-closed'}
                  onCameraReady={() => {
                    videoRef.current = document.querySelector('video');
                    if (hasFaceDetectionEnabled && !landmarkerInitFailed) {
                      startVideoValidation();
                      return;
                    }
                    setIsFaceDetected(true);
                    if (!countdownTimerRef.current) {
                      startCountdown();
                    }
                  }}
                  facing={(ipad?.settingsPictureCamera.toLowerCase() as CameraType) || 'front'}
                  style={{ flex: 1 }}
                />

                {hasFaceDetectionEnabled && !landmarkerInitFailed && (
                  <>
                    {faceLiveness ? (
                      <LivenessMask
                        activeLeft={feedback === t('turn.right')}
                        activeRight={feedback === t('turn.left')}
                      />
                    ) : (
                      <FaceScanner isFaceDetected={isFaceDetected} colors={colors} />
                    )}
                  </>
                )}

                {countdown !== null && countdown > 0 && (
                  <Text style={styles.countdownText}>{countdown}</Text>
                )}

                {(feedback || isFaceDetected) && (
                  <View
                    style={[
                      styles.feedbackBoxBase,
                      { backgroundColor: isFaceDetected ? colors.success : colors.primary },
                    ]}
                  >
                    <Text size={fontSizes.lg2} fontType="bold" style={styles.feedbackText}>
                      {isFaceDetected && !faceLiveness ? t('stayStill') : feedback}
                    </Text>
                  </View>
                )}

                <Button
                  backgroundColor={customization?.mainColor || colors.primary}
                  buttonStyles={{ marginVertical: 8 }}
                  onPress={() => captureFrame(faceRef)}
                  disabled={hasFaceDetectionEnabled && !landmarkerInitFailed && !isFaceDetected}
                >
                  {t('takePicture')}
                </Button>

                <Button
                  outline
                  onPress={() => {
                    resetStates();
                    setIsStartPicture(false);
                    setIsFaceDetected(false);
                    if (!showPhotoInstructionsKiosk) {
                      navigation.goBack();
                    } else {
                      setStep('INSTRUCTION_INITIAL');
                    }
                  }}
                >
                  {t('Cancel')}
                </Button>
              </View>

              {isCapturingVisualState === true && (
                <View
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 50,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                    borderRadius: 8,
                  }}
                >
                  <ActivityIndicator color={colors.white} size="large" />
                  <Text
                    fontType="bold"
                    size={fontSizes.lg}
                    style={{ color: colors.white, textAlign: 'center' }}
                  >
                    {t('validatingPhoto')}
                  </Text>
                </View>
              )}
            </BlurView>
          </View>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
      </ContainerWidthLimit>
    </View>
  );
};

export default PictureScreenWeb;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white, alignItems: 'center' },
  content: { flex: 1, paddingHorizontal: 32, paddingBottom: 40 },
  innerContentShadow: { flex: 1, borderRadius: 8, overflow: 'hidden', ...defaultShadow },
  picturePlaceholderBox: {
    backgroundColor: 'transparent',
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    zIndex: 10,
    flexDirection: 'row',
  },
  picturePlaceholderBorder: {
    flex: Platform.OS === 'web' ? 1 : 0,
    backgroundColor: '#323438',
    opacity: 0.3,
  },
  picturePlaceholder: {
    ...Platform.select({ web: { width: '100%' }, ios: { flex: 1 }, android: { flex: 1 } }),
    height: '100%',
  },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  overlayContainer: {
    flex: 1,
    zIndex: 900,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  blurContainer: { padding: 40, flex: 1, alignItems: 'center', justifyContent: 'center' },
  countdownText: {
    position: 'absolute',
    top: '40%',
    left: '50%',
    fontSize: 64,
    fontWeight: 'bold',
    transform: [{ translateX: -32 }, { translateY: -32 }],
    color: '#fff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
    zIndex: 20,
  },
  feedbackBoxBase: {
    position: 'absolute',
    top: '5%',
    left: '50%',
    transform: [{ translateX: '-50%' }],
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    maxWidth: 300,
    zIndex: 30,
    shadowColor: 'rgba(0, 0, 0, 0.3)',
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 8,
  },
  feedbackText: { textAlign: 'center', color: '#fff' },
  previewContainer: { flex: 1, backgroundColor: '#F2F4F7' },
  previewInner: { flex: 1, margin: 24, borderRadius: 26, overflow: 'hidden' },
  fullImage: { flex: 1 },
});
